import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import {
  Bot,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
  ThumbsDown,
  ThumbsUp,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { Tool, ToolHeader, ToolContent, ToolInput, ToolOutput } from "@/components/ai-elements/tool";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/helpdesk-chat`;
const STORAGE_KEY = "td_helpdesk_conv";
/** Client-side cap on a single chat message (mirrors the ticket form's pattern). */
const MAX_CHAT_CHARS = 2000;

type Citation = { title: string; slug: string; url: string | null };

const QUICK_REPLIES = [
  "Where are my alerts?",
  "Refund policy",
  "My license isn't active",
] as const;

const SEED: UIMessage[] = [
  {
    id: "seed-1",
    role: "assistant",
    parts: [
      {
        type: "text",
        text:
          "Hi! I'm the Trends Discovery assistant. Ask me about your license, the trend feed, alerts, or billing. If I can't help, I can open a ticket with the human team.",
      },
    ],
  } as UIMessage,
];

type WidgetProps = {
  variant?: "floating" | "inline";
  className?: string;
  defaultOpen?: boolean;
};

export default function HelpdeskWidget({
  variant = "floating",
  className,
  defaultOpen = false,
}: WidgetProps) {
  const [open, setOpen] = useState(defaultOpen || variant === "inline");
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState<Record<string, boolean>>({});
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [pendingEscalation, setPendingEscalation] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      setUserId(data.user?.id ?? null);
      setUserEmail(data.user?.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
      setUserEmail(session?.user?.email ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);


  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: ENDPOINT,
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        prepareSendMessagesRequest({ messages, id }) {
          const conversationId =
            typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
          return { body: { messages, id, conversationId } };
        },
        fetch: async (input, init) => {
          const res = await fetch(input, init);
          const convId = res.headers.get("x-conversation-id");
          if (convId && typeof window !== "undefined") {
            localStorage.setItem(STORAGE_KEY, convId);
          }
          return res;
        },
      }),
    [],
  );

  const { messages, sendMessage, status, error, regenerate, setMessages } = useChat({
    transport,
    messages: SEED,
  });

  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    if (open) requestAnimationFrame(() => taRef.current?.focus());
  }, [open]);

  const submit = useCallback(
    async (e?: React.FormEvent, override?: string) => {
      e?.preventDefault();
      const text = (override ?? input).trim();
      if (!text || busy) return;
      if (text.length > MAX_CHAT_CHARS) {
        toast.error(
          `Message is too long (${text.length.toLocaleString()} characters). Please keep it under ${MAX_CHAT_CHARS.toLocaleString()}.`,
        );
        return;
      }
      if (!override) setInput("");
      await sendMessage({ text });
      requestAnimationFrame(() => taRef.current?.focus());
    },
    [busy, input, sendMessage],
  );

  const requestEscalation = useCallback(
    (prefill?: string) => {
      if (!userId) {
        const next = typeof window !== "undefined" ? window.location.pathname : "/support";
        toast.info("Please sign in to open a support ticket.", {
          action: {
            label: "Sign in",
            onClick: () => {
              window.location.href = `/auth?next=${encodeURIComponent(next)}`;
            },
          },
        });
        return;
      }
      const lastUser = [...messages].reverse().find((m) => m.role === "user");
      const summary =
        prefill?.trim() ||
        (lastUser?.parts ?? [])
          .map((p: any) => (p.type === "text" ? p.text : ""))
          .join(" ")
          .trim() ||
        "I'd like to talk to a human about my Trends Discovery account.";
      setPendingEscalation(summary);
    },
    [messages, userId],
  );

  const confirmEscalation = useCallback(() => {
    const summary = pendingEscalation;
    setPendingEscalation(null);
    if (!summary) return;
    void submit(
      undefined,
      `Please open a support ticket for me. Summary of my issue: ${summary}`,
    );
  }, [pendingEscalation, submit]);

  const handleCopy = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  }, []);

  const recordFeedback = useCallback(
    async (messageId: string, helpful: boolean, reasonText?: string) => {
      const conversationId =
        typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      // Collect retrieved kb_chunk IDs from the assistant message's tool parts
      // so weekly review can spot chunks that keep producing bad answers.
      const msg = messages.find((m) => m.id === messageId);
      const retrieved_ids: string[] = [];
      for (const p of (msg?.parts ?? []) as any[]) {
        const results = p?.output?.results;
        if (Array.isArray(results)) {
          for (const r of results) if (r?.chunk_id) retrieved_ids.push(r.chunk_id);
        }
      }
      try {
        await supabase.from("helpdesk_feedback").insert({
          conversation_id: conversationId,
          message_id: messageId,
          helpful,
          retrieved_ids,
          ...(reasonText ? { comment: reasonText } : {}),
        });
      } catch (e) {
        console.error(e);
      }
    },
    [messages],
  );

  const sendFeedback = useCallback(
    async (messageId: string, helpful: boolean) => {
      if (feedback[messageId] !== undefined) return;
      setFeedback((f) => ({ ...f, [messageId]: helpful }));
      if (helpful) {
        await recordFeedback(messageId, true);
        toast.success("Thanks for the feedback!");
      } else {
        setReasonFor(messageId);
        setReason("");
      }
    },
    [feedback, recordFeedback],
  );

  const submitReason = useCallback(async () => {
    const mid = reasonFor;
    const r = reason.trim();
    if (!mid) return;
    setReasonFor(null);
    setReason("");
    await recordFeedback(mid, false, r || undefined);
    toast.success("Thanks — we'll improve this.");
    requestEscalation(r || undefined);
  }, [reason, reasonFor, recordFeedback, requestEscalation]);

  const renderMessage = (m: UIMessage, idx: number) => {
    const text = (m.parts ?? [])
      .map((p: any) => (p.type === "text" ? p.text : ""))
      .join("");
    const toolParts = (m.parts ?? []).filter((p: any) =>
      typeof p.type === "string" && p.type.startsWith("tool-"),
    );
    const isUser = m.role === "user";
    const isLastAssistant =
      !isUser && idx === messages.length - 1 && status !== "streaming";

    // Collect citation chips from kb_search tool output OR seed citations
    const citations: Citation[] = [];
    for (const tp of toolParts as any[]) {
      const out = tp.output;
      const results = out?.results;
      if (Array.isArray(results)) {
        for (const r of results) {
          if (r?.title)
            citations.push({ title: r.title, slug: r.slug, url: r.url ?? null });
        }
      }
    }

    return (
      <Message key={m.id} from={m.role as "user" | "assistant"} className="!py-2">
        {!isUser && (
          <div className="flex flex-col gap-2 w-full">
            <MessageContent className="!py-0 bg-transparent">
              {toolParts.map((tp: any) => {
                const name = tp.type?.replace(/^tool-/, "") ?? "tool";
                const state = tp.state ?? "input-available";
                const uiState =
                  state === "output-available"
                    ? "output-available"
                    : state === "output-error"
                      ? "output-error"
                      : state === "input-available"
                        ? "input-available"
                        : "input-streaming";
                return (
                  <Tool key={tp.toolCallId ?? `${m.id}-${name}`} defaultOpen={false}>
                    <ToolHeader type={`tool-${name}`} state={uiState as any} />
                    <ToolContent>
                      <ToolInput input={tp.input} />
                      <ToolOutput output={JSON.stringify(tp.output ?? null, null, 2)} errorText={tp.errorText} />
                    </ToolContent>
                  </Tool>
                );
              })}
              {text ? <MessageResponse>{text}</MessageResponse> : null}
            </MessageContent>

            {citations.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {citations.slice(0, 6).map((c, i) => (
                  <a
                    key={`${m.id}-cite-${i}`}
                    href={c.url ?? `/support#${c.slug}`}
                    target={c.url ? "_blank" : undefined}
                    rel="noreferrer"
                    className="no-underline"
                  >
                    <Badge variant="secondary" className="gap-1 text-[10px] font-medium">
                      [{i + 1}] {c.title}
                      <ExternalLink className="size-2.5" />
                    </Badge>
                  </a>
                ))}
              </div>
            )}

            {!isUser && text && idx > 0 && (
              <div className="flex items-center gap-1 mt-1 -ml-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="size-7 text-muted-foreground"
                  onClick={() => handleCopy(m.id, text)}
                  aria-label="Copy"
                >
                  {copiedId === m.id ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                </Button>
                {isLastAssistant && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-7 text-muted-foreground"
                    onClick={() => regenerate()}
                    aria-label="Regenerate"
                  >
                    <RefreshCw className="size-3.5" />
                  </Button>
                )}
                <div className="flex items-center gap-0.5 ml-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={cn(
                      "size-7 text-muted-foreground",
                      feedback[m.id] === true && "text-primary",
                    )}
                    onClick={() => sendFeedback(m.id, true)}
                    aria-label="Helpful"
                    disabled={feedback[m.id] !== undefined}
                  >
                    <ThumbsUp className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={cn(
                      "size-7 text-muted-foreground",
                      feedback[m.id] === false && "text-destructive",
                    )}
                    onClick={() => sendFeedback(m.id, false)}
                    aria-label="Not helpful"
                    disabled={feedback[m.id] !== undefined}
                  >
                    <ThumbsDown className="size-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {isUser && (
          <MessageContent>
            <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {text}
            </span>
          </MessageContent>
        )}
      </Message>
    );
  };

  const isFirstTurn = messages.length <= 1 && status === "ready";

  const ChatBody = (
    <>
      <Conversation className="flex-1">
        <ConversationContent className="px-4 py-4">
          {messages.map((m, idx) => renderMessage(m, idx))}

          {isFirstTurn && (
            <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => void submit(undefined, q)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-accent hover:text-accent-foreground transition"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {status === "submitted" && (
            <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
              <Bot className="size-3.5" />
              <Shimmer>Thinking…</Shimmer>
            </div>
          )}
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-2">
              Something went wrong. Try again, or click "Talk to a human" below.
            </div>
          )}

          {reasonFor && (
            <div className="mt-2 rounded-lg border border-border bg-muted/40 p-3 space-y-2">
              <div className="text-xs font-medium">What went wrong? (optional)</div>
              <input
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 240))}
                placeholder="e.g. answer didn't match my situation"
                className="w-full text-sm bg-background border border-border rounded-md px-2 py-1.5"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submitReason();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setReasonFor(null)}>
                  Skip
                </Button>
                <Button size="sm" onClick={() => void submitReason()}>
                  Send &amp; talk to a human
                </Button>
              </div>
            </div>
          )}

          {pendingEscalation && (
            <div className="mt-2 rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
              <div className="text-xs font-semibold text-foreground">
                Open a ticket with the human team?
              </div>
              <div className="text-xs text-muted-foreground">
                We'll email updates to{" "}
                <span className="font-medium text-foreground">{userEmail ?? "your account"}</span>{" "}
                and reply within 24 hours.
              </div>
              <div className="text-xs bg-background rounded-md border border-border p-2 whitespace-pre-wrap max-h-24 overflow-auto">
                {pendingEscalation}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPendingEscalation(null)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={confirmEscalation}>
                  Yes, open ticket
                </Button>
              </div>
            </div>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <form onSubmit={submit} className="border-t border-border p-3 bg-card">
        <div className="flex items-end gap-2">
          <Textarea
            ref={taRef}
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, MAX_CHAT_CHARS))}
            placeholder="Ask about your license, the feed, alerts…"
            rows={1}
            maxLength={MAX_CHAT_CHARS}
            aria-describedby="helpdesk-char-count"
            className="resize-none min-h-[40px] max-h-32"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Button
            type="submit"
            size="icon"
            disabled={busy || !input.trim() || input.trim().length > MAX_CHAT_CHARS}
            aria-label="Send"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          </Button>
        </div>
        <div
          id="helpdesk-char-count"
          data-testid="helpdesk-char-count"
          className={cn(
            "mt-1 px-1 text-right text-[10px] tabular-nums",
            input.length >= MAX_CHAT_CHARS ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {input.length}/{MAX_CHAT_CHARS}
        </div>
        <div className="flex items-center justify-between mt-2 px-1">
          <button
            type="button"
            onClick={() => requestEscalation()}
            disabled={busy}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition"
          >
            <UserRound className="size-3" />
            {userId ? "Talk to a human" : "Sign in to open a ticket"}
          </button>

          <div className="text-[10px] text-muted-foreground">
            AI can make mistakes. Verify important info.
          </div>
        </div>
      </form>
    </>
  );


  if (variant === "inline") {
    return (
      <div
        className={cn(
          "flex flex-col bg-card border border-border rounded-2xl shadow-sm overflow-hidden h-[70vh] min-h-[480px]",
          className,
        )}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Bot className="size-4 text-primary" />
            </div>
            <div>
              <div className="font-display font-semibold text-sm leading-tight">
                Trends Discovery assistant
              </div>
              <div className="text-[11px] text-muted-foreground">
                Grounded in our docs · escalates to humans
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (typeof window !== "undefined") localStorage.removeItem(STORAGE_KEY);
              setMessages(SEED);
              setFeedback({});
            }}
          >
            New chat
          </Button>
        </header>
        {ChatBody}
      </div>
    );
  }

  return (
    <>
      {!open && (
        <Button
          variant="hero"
          size="lg"
          className="fixed bottom-6 right-6 z-40 shadow-lg rounded-full pl-4 pr-5 gap-2"
          onClick={() => setOpen(true)}
          aria-label="Open chat assistant"
        >
          <MessageCircle className="size-5" />
          Ask Trends Helper
        </Button>
      )}

      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:right-6 sm:bottom-6 z-40 w-auto sm:w-[400px] h-[100dvh] sm:h-[600px] bg-card border border-border sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
            <div className="flex items-center gap-2">
              <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="size-4 text-primary" />
              </div>
              <div>
                <div className="font-display font-semibold text-sm leading-tight">
                  Support assistant
                </div>
                <div className="text-[11px] text-muted-foreground">
                  AI · grounded in our docs
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close">
              <X className="size-4" />
            </Button>
          </header>
          {ChatBody}
        </div>
      )}
    </>
  );
}
